import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail, CheckCircle2, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import InitialsAvatar from "@/components/InitialsAvatar";
import { useI18n } from "@/i18n/I18nContext";
import { findMemberBySlug, TEAM_MEMBERS } from "@/data/team";

const LABELS = {
  tr: { back: "Ekibimiz", expertise: "Uzmanlık Alanları", contact: "İletişim", emailLabel: "E-posta", others: "Diğer Ekip Üyeleri", view: "Görüntüle" },
  en: { back: "Our Team", expertise: "Areas of Expertise", contact: "Contact", emailLabel: "Email", others: "Other Team Members", view: "View" },
  ar: { back: "فريقنا", expertise: "مجالات الخبرة", contact: "تواصل", emailLabel: "البريد", others: "أعضاء آخرون", view: "عرض" },
  es: { back: "Nuestro Equipo", expertise: "Áreas de Experiencia", contact: "Contacto", emailLabel: "Email", others: "Otros Miembros", view: "Ver" },
  ru: { back: "Наша команда", expertise: "Области экспертизы", contact: "Контакты", emailLabel: "Email", others: "Другие члены", view: "Профиль" },
  de: { back: "Unser Team", expertise: "Fachgebiete", contact: "Kontakt", emailLabel: "E-Mail", others: "Weitere Teammitglieder", view: "Ansehen" },
  fr: { back: "Notre Équipe", expertise: "Domaines d'expertise", contact: "Contact", emailLabel: "E-mail", others: "Autres membres", view: "Voir" },
};

const TeamMemberPage = () => {
  const { slug } = useParams();
  const { locale } = useI18n();
  const member = findMemberBySlug(slug);
  const lab = LABELS[locale] || LABELS.en;

  useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); }, [slug]);

  if (!member) return <Navigate to="/ekibimiz" replace />;

  const others = TEAM_MEMBERS.filter((m) => m.slug !== member.slug);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid={`team-member-page-${member.slug}`}>
      <Navbar />
      <main>
        <section className="relative hero-wash">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 md:pt-20 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.55 }} className="lg:col-span-5">
              <Link to="/ekibimiz" className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.14em] uppercase text-slate-500 hover:text-[hsl(var(--primary))]">
                <ArrowLeft className="h-3.5 w-3.5" /> {lab.back}
              </Link>
              <div className="mt-4 relative">
                <div className="absolute -inset-4 -z-10 rounded-[28px] bg-[hsl(var(--accent))]/10 blur-2xl" />
                <div className="overflow-hidden rounded-3xl border border-[rgba(201,162,39,0.3)] aspect-square bg-slate-100">
                  {member.photo ? (
                    <img src={member.photo} alt={member.name} className="w-full h-full object-cover" loading="eager" />
                  ) : (
                    <InitialsAvatar initials={member.initials} accent={member.accent} />
                  )}
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-7">
              <div className="text-[11px] font-semibold tracking-[0.18em] text-[hsl(var(--accent))] uppercase">
                {member.role[locale] || member.role.en}
              </div>
              <h1 className="mt-3 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[hsl(var(--primary))]" data-testid="member-name">
                {member.name}
              </h1>
              <div className="gold-rule mt-6 max-w-32" />
              <p className="mt-6 text-base md:text-lg leading-relaxed text-slate-700">
                {member.bio[locale] || member.bio.en}
              </p>

              <div className="mt-8">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{lab.expertise}</div>
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {(member.expertise[locale] || member.expertise.en).map((x) => (
                    <li key={x} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-[hsl(var(--accent))] shrink-0" />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Card className="mt-8 bg-[hsl(var(--primary))] text-white border-0 rounded-2xl p-6 relative overflow-hidden">
                <div className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-[hsl(var(--accent))]/15" />
                <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <div className="text-[11px] font-semibold tracking-[0.14em] text-[hsl(var(--accent))] uppercase">{lab.contact}</div>
                    <div className="mt-1 font-display text-xl">{lab.emailLabel}</div>
                    <a href={`mailto:${member.contact.email}`} className="text-white/85 hover:text-[hsl(var(--accent))] text-sm break-all" data-testid="member-email-link">
                      {member.contact.email}
                    </a>
                  </div>
                  <Button asChild className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--tbs-gold-600))] text-[hsl(var(--primary))] font-semibold rounded-xl h-11 px-5">
                    <a href={`mailto:${member.contact.email}`}>
                      <Mail className="mr-2 h-4 w-4" />
                      {lab.emailLabel}
                    </a>
                  </Button>
                </div>
              </Card>
            </motion.div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-[hsl(var(--tbs-cream-50))]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-[11px] font-semibold tracking-[0.18em] text-[hsl(var(--accent))] uppercase">{lab.others}</div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
              {others.map((o) => (
                <Link key={o.slug} to={`/ekibimiz/${o.slug}`} data-testid={`other-member-${o.slug}`}>
                  <Card className="card-lift bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4">
                    <div className="h-16 w-16 rounded-xl overflow-hidden shrink-0 bg-slate-100">
                      {o.photo ? (
                        <img src={o.photo} alt={o.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <InitialsAvatar initials={o.initials} accent={o.accent} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-semibold tracking-[0.12em] text-[hsl(var(--accent))] uppercase truncate">{o.role[locale] || o.role.en}</div>
                      <div className="font-display text-lg text-[hsl(var(--primary))] truncate">{o.name}</div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-slate-400 shrink-0" />
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default TeamMemberPage;
